import { CreditDot, LOW_BALANCE } from './credit-chip';
import { useCredits } from './use-credits';

/**
 * The balance, in the top bar beside the fix list.
 *
 * **Deliberately the fix list's opposite.** That one is a solid ink pill with a
 * lime badge — work owed, and it wants to be pressed. This is the same size and
 * the same corner radius on a light surface with a hairline edge: a figure you
 * hold rather than a task you owe, and nothing to click, because there is
 * nothing here to do. They read as one family at a glance and are never
 * mistaken for each other, which two solid pills side by side would be.
 *
 * Not a button. Topping up is flotoapps' and there is no screen here to send
 * anybody to, so an affordance would be a promise the product does not keep.
 *
 * Renders nothing until the balance has actually arrived — the same rule
 * `UserMenu` follows. A zero flashed at somebody with plenty is worse than a
 * beat of nothing, because the one thing this element must never do is make a
 * user think they cannot afford what they are about to press.
 */
export function CreditsPill() {
  const { balance, loaded } = useCredits();

  if (!loaded) return null;

  const low = balance.totalCredits < LOW_BALANCE;

  return (
    <div
      // A live region: the number changes on its own, seconds after a run is
      // started from a sheet that covers this bar. Polite, so it is announced
      // after whatever the user is doing rather than interrupting it.
      aria-live="polite"
      className={[
        'flex h-9 shrink-0 items-center gap-2 rounded-pill border bg-surface px-3 whitespace-nowrap',
        'text-ui font-semibold tabular-nums transition-colors duration-300',
        low
          ? 'border-brand/30 text-brand'
          : 'border-hairline text-text-primary',
      ].join(' ')}
      title={
        low
          ? 'Running low — feedback runs cost credits'
          : 'Credits available for feedback runs'
      }
    >
      <CreditDot tone={low ? 'alarm' : 'accent'} />
      <span>{balance.totalCredits}</span>
      {/* The word only when there is room. The dot and the pill's company —
          a fix-list count right beside it — carry the meaning on a narrow
          board, and the accessible name below always spells it out. */}
      <span className="hidden text-text-muted xl:inline">Credits</span>
      <span className="sr-only">
        credits remaining{low ? ', running low' : ''}
      </span>
    </div>
  );
}
